// Who can post to this account, and why.
//
// The owner's question is never "does BluePeak have access" — it is "which
// humans can publish to my Facebook Page". ADR-0015 made that distinction for
// engagements; a share makes it sharper still, because a share is addressed to
// an ORGANISATION and the set of people behind it changes when their roster
// does, without the owner doing anything.
//
// That is a real cost of org-addressed sharing, and the answer is not to hide
// it. The people are listed by name, always, next to the organisation they come
// from — so "who can reach this" has a current answer rather than a structural
// one.
//
// Three routes in, listed separately: a merged list answers "who" and loses
// "why", and "why" is the second question every time.

import { useCallback, useEffect, useState } from "react";
import { Loading, Spinner } from "./Spinner";
import {
  connectionAccess, revokeConnectionShare, shareConnection,
  type ConnectionAccess, type Organisation,
} from "../lib/organisation";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/** What an account may be lent at. Never owner or admin — those carry
 *  membership administration and the right to disconnect, neither of which is
 *  the lender's to give away with one account. */
const LENDABLE = [
  { code: "publisher", label: "Publisher", blurb: "Can publish and schedule to this account." },
  { code: "creator", label: "Creator", blurb: "Can draft and submit, but not publish." },
  { code: "analyst", label: "Analyst", blurb: "Can see results. Publishes nothing." },
];

export function ConnectionAccessPanel({
  connectionId, displayName, organisations, mayShare, onChanged,
}: {
  connectionId: string;
  displayName: string;
  organisations: Organisation[];
  mayShare: boolean;
  onChanged?: () => void;
}) {
  const [access, setAccess] = useState<ConnectionAccess | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const busy = busyKey !== null;

  const [toOrg, setToOrg] = useState("");
  const [role, setRole] = useState("publisher");
  const [ends, setEnds] = useState("");

  const load = useCallback(async () => {
    setAccess(await connectionAccess(connectionId));
  }, [connectionId]);

  useEffect(() => {
    if (!open) return;
    load().catch((e) => setError(e.message ?? String(e)));
  }, [open, load]);

  async function run(fn: () => Promise<unknown>, key: string) {
    setBusyKey(key);
    setError(null);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusyKey(null);
    }
  }

  const shareCount = access?.shared_with?.length ?? 0;
  const borrowers = access?.shared_with?.reduce((n, s) => n + (s.people?.length ?? 0), 0) ?? 0;

  return (
    <div className="conn-access">
      <button type="button" className="link" aria-expanded={open}
              onClick={() => setOpen((v) => !v)}>
        {open ? "Hide who can post to this" : "Who can post to this?"}
      </button>

      {open && (
        <div className="card" style={{ marginTop: ".6rem" }}>
          {error && <p className="err">{error}</p>}
          {!access ? <Loading label="Working out who can reach this account…" /> : (
            <>
              <div className="card-head">
                <h2>Access to {displayName}</h2>
                {shareCount > 0 && (
                  <span className="chip warn">
                    Lent to {shareCount === 1 ? "1 organisation" : `${shareCount} organisations`}
                  </span>
                )}
              </div>

              <h3 className="conn-access-head">In this workspace</h3>
              {access.in_workspace.length === 0
                ? <p className="muted">Nobody but you.</p>
                : (
                  <ul className="can-list">
                    {access.in_workspace.map((p) => (
                      <li className="can" key={`${p.user_id}-${p.role}`}>
                        <strong>{p.email ?? p.user_id}</strong> — {p.role}
                        {p.scope === "connection" && " · only this account"}
                      </li>
                    ))}
                  </ul>
                )}

              {access.via_engagement.length > 0 && (
                <>
                  <h3 className="conn-access-head">Through an agency you authorised</h3>
                  <ul className="can-list">
                    {access.via_engagement.map((p) => (
                      <li className="can" key={`${p.user_id}-${p.role}`}>
                        <strong>{p.email ?? p.user_id}</strong> — {p.role} · {p.organisation}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <h3 className="conn-access-head">Lent to another organisation</h3>
              {shareCount === 0 ? (
                <p className="muted">
                  This account is not shared with anyone. It is yours alone.
                </p>
              ) : (
                <>
                  {/* The sentence that makes org-addressed sharing honest. */}
                  <p className="notice card-sub">
                    You lent this to an <strong>organisation</strong>, so its people are whoever is
                    on their roster today — {borrowers === 1 ? "1 person" : `${borrowers} people`}{" "}
                    right now. They choose who; you choose whether.
                  </p>
                  {access.shared_with.map((s) => (
                    <div className="outside-group" key={s.share_id}>
                      <header className="outside-group-head">
                        <div>
                          <strong>{s.organisation}</strong>
                          <span className="muted"> · {s.role}</span>
                        </div>
                        <div className="outside-group-when">
                          {s.expires_at
                            ? <span className="chip">Until {shortDate(s.expires_at)}</span>
                            : <span className="chip warn">No end date</span>}
                        </div>
                      </header>
                      <ul className="can-list">
                        {s.people.map((p) => (
                          <li className="can" key={p.user_id}>
                            <strong>{p.email ?? p.user_id}</strong>
                            <span className="muted"> · {p.org_role} there</span>
                          </li>
                        ))}
                      </ul>
                      <p className="muted" style={{ fontSize: ".8rem" }}>
                        They cannot disconnect or reconnect this account, cannot reach your other
                        accounts, and cannot lend it on.
                      </p>
                      {mayShare && (
                        <button
                          className={busyKey === `revoke:${s.share_id}` ? "sm danger is-busy" : "sm danger"}
                          disabled={busy}
                          onClick={() => {
                            if (!confirm(
                              `Stop sharing ${displayName} with ${s.organisation}?\n\n`
                              + `All ${s.people.length} of their people lose access immediately. `
                              + "Anything already published stays published.")) return;
                            void run(() => revokeConnectionShare(s.share_id), `revoke:${s.share_id}`);
                          }}
                        >
                          {busyKey === `revoke:${s.share_id}` && <Spinner />}
                          {busyKey === `revoke:${s.share_id}` ? "Ending…" : "Stop sharing"}
                        </button>
                      )}
                    </div>
                  ))}
                </>
              )}

              {mayShare && organisations.length > 0 && (
                <>
                  <h3 className="conn-access-head">Lend it to an organisation</h3>
                  <div className="invite-form">
                    <div>
                      <label htmlFor={`share-org-${connectionId}`}>Which organisation</label>
                      <select id={`share-org-${connectionId}`} value={toOrg}
                              onChange={(e) => setToOrg(e.target.value)}>
                        <option value="">Choose…</option>
                        {organisations.map((o) => (
                          <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`share-role-${connectionId}`}>What they may do</label>
                      <select id={`share-role-${connectionId}`} value={role}
                              onChange={(e) => setRole(e.target.value)}>
                        {LENDABLE.map((r) => (
                          <option key={r.code} value={r.code}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`share-ends-${connectionId}`}>Ends (optional)</label>
                      <input id={`share-ends-${connectionId}`} type="date" value={ends}
                             onChange={(e) => setEnds(e.target.value)} />
                    </div>
                  </div>
                  <p className="muted card-sub">
                    {LENDABLE.find((r) => r.code === role)?.blurb}{" "}
                    They never receive the account's credentials, and cannot disconnect it or lend
                    it to anyone else.
                  </p>
                  <button
                    className={busyKey === "share" ? "primary is-busy" : "primary"}
                    disabled={busy || !toOrg}
                    onClick={() => void run(async () => {
                      await shareConnection({
                        connectionId,
                        toOrganisationId: toOrg,
                        role,
                        expiresAt: ends ? new Date(`${ends}T23:59:59`).toISOString() : null,
                      });
                      setToOrg("");
                      setEnds("");
                    }, "share")}
                  >
                    {busyKey === "share" && <Spinner />}
                    {busyKey === "share" ? "Sharing…" : "Share this account"}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
